import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { ConsentStatusItem } from '@/api/consents/consentsApi.types';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { SafeHtml } from '@/components/common/SafeHtml';
import { ConsentDecisionToggle } from '@/components/common/ConsentDecisionToggle';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import { CONSENT_DECISION, type ConsentDecision } from '@/constants/consents';
import {
  useCaptureClientConsentsMutation,
  useClientConsentsQuery,
  useRequestClientConsentsMutation,
} from '@/hooks/useClientConsents';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface ConsentsPanelProps {
  clientId: string;
}

export function ConsentsPanel({ clientId }: ConsentsPanelProps) {
  const { t } = useTranslation('clients');
  const consentsQuery = useClientConsentsQuery(clientId);
  const requestMutation = useRequestClientConsentsMutation(clientId);
  const [isRecording, setIsRecording] = useState(false);

  const items = consentsQuery.data === undefined ? [] : consentsQuery.data;

  function requestConsents() {
    requestMutation.mutate(undefined, {
      onSuccess: function done() {
        showSuccessNotification(t('consents.requestSent'));
      },
      onError: function fail(requestError) {
        const message = resolveApiErrorMessage(requestError);
        showErrorNotification(message.length > 0 ? message : t('consents.requestError'));
      },
    });
  }

  return (
    <>
      <RelatedRecordsSection
        title={t('consents.title')}
        isLoading={consentsQuery.isLoading}
        isError={consentsQuery.isError}
        onRetry={function retry() {
          consentsQuery.refetch();
        }}
        actions={
          <>
            <Button
              size="small"
              variant="outline"
              isLoading={requestMutation.isPending}
              onClick={requestConsents}
            >
              <Icon name="Mail" /> {t('consents.request')}
            </Button>
            <Button
              size="small"
              variant="outline"
              disabled={items.length === 0}
              onClick={function openRecord() {
                setIsRecording(true);
              }}
            >
              <Icon name="Send" /> {t('consents.record')}
            </Button>
          </>
        }
      >
        {items.length === 0 ? (
          <p className="text-sm text-zoomuted py-2">{t('consents.empty')}</p>
        ) : (
          <div className="divide-y divide-zooborder">
            {items.map(function renderRow(item) {
              return <ConsentStatusRow key={item.documentId} item={item} />;
            })}
          </div>
        )}
      </RelatedRecordsSection>

      {isRecording === true ? (
        <RecordConsentModal
          clientId={clientId}
          items={items}
          onClose={function close() {
            setIsRecording(false);
          }}
        />
      ) : null}
    </>
  );
}

function ConsentStatusRow({ item }: { item: ConsentStatusItem }) {
  const { t } = useTranslation('clients');
  return (
    <div className="flex items-center gap-2 py-2.5 text-[13px]">
      <ConsentMark decision={item.decision} />
      <span className="text-zooindigo font-medium flex-1 min-w-0 truncate">{item.title}</span>
      {item.needsReacceptance === true ? (
        <span className="text-zoowarningtext text-[11px] font-bold shrink-0">
          {t('consents.updateNeeded')}
        </span>
      ) : null}
      <span className="text-zoomuted text-xs shrink-0">
        {item.decision === null
          ? t('consents.notCaptured')
          : item.decidedAt === null
            ? ''
            : formatDateToDayMonthYear(new Date(item.decidedAt))}
      </span>
    </div>
  );
}

function ConsentMark({ decision }: { decision: string | null }) {
  const { t } = useTranslation('clients');
  if (decision === CONSENT_DECISION.agreed) {
    return (
      <span role="img" aria-label={t('consents.agreed')} className="text-zoosuccesstext shrink-0">
        <Icon name="Check" />
      </span>
    );
  }
  if (decision === CONSENT_DECISION.declined) {
    return (
      <span role="img" aria-label={t('consents.declined')} className="text-zooerrortext shrink-0">
        <Icon name="X" />
      </span>
    );
  }
  if (decision === CONSENT_DECISION.withdrawn) {
    return (
      <span
        role="img"
        aria-label={t('consents.withdrawnStatus')}
        className="text-zoowarningtext shrink-0"
      >
        <Icon name="X" />
      </span>
    );
  }
  return (
    <span role="img" aria-label={t('consents.notCaptured')} className="text-zoodisabledtext shrink-0">
      <Icon name="Clock" />
    </span>
  );
}

interface RecordConsentModalProps {
  clientId: string;
  items: ConsentStatusItem[];
  onClose: () => void;
}

function RecordConsentModal({ clientId, items, onClose }: RecordConsentModalProps) {
  const { t } = useTranslation(['clients', 'common']);
  const captureMutation = useCaptureClientConsentsMutation(clientId);
  const [decisions, setDecisions] = useState<Record<string, ConsentDecision>>(function initial() {
    const start: Record<string, ConsentDecision> = {};
    items.forEach(function seed(item) {
      start[item.documentId] =
        item.decision === CONSENT_DECISION.declined ||
        item.decision === CONSENT_DECISION.withdrawn
          ? CONSENT_DECISION.declined
          : CONSENT_DECISION.agreed;
    });
    return start;
  });
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null);

  function save() {
    const captureItems = items.map(function toItem(item) {
      const decision =
        decisions[item.documentId] === CONSENT_DECISION.declined
          ? CONSENT_DECISION.declined
          : CONSENT_DECISION.agreed;
      return {
        documentId: item.documentId,
        documentVersionId: item.currentVersionId,
        decision: decision,
      };
    });
    captureMutation.mutate(captureItems, {
      onSuccess: function done() {
        showSuccessNotification(t('clients:consents.saved'));
        onClose();
      },
      onError: function fail(saveError) {
        const backendMessage = resolveApiErrorMessage(saveError);
        showErrorNotification(
          backendMessage.length > 0 ? backendMessage : t('clients:consents.saveError')
        );
      },
    });
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="w-full max-w-lg bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">{t('clients:consents.recordTitle')}</h2>
          <button type="button" aria-label={t('common:actions.close')} onClick={onClose}>
            <Icon name="X" className="text-zoomuted" />
          </button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto">
          {items.map(function renderRow(item) {
            const value = decisions[item.documentId];
            const isExpanded = expandedDocumentId === item.documentId;
            return (
              <div key={item.documentId} className="border border-zooborder rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left text-sm font-medium text-zooindigo truncate"
                    onClick={function toggleExpand() {
                      setExpandedDocumentId(isExpanded === true ? null : item.documentId);
                    }}
                  >
                    {item.title}
                  </button>
                  <ConsentDecisionToggle
                    value={value === undefined ? null : value}
                    size="sm"
                    onSelect={function pick(choice) {
                      setDecisions(function update(previous) {
                        return { ...previous, [item.documentId]: choice };
                      });
                    }}
                  />
                </div>
                {isExpanded === true ? (
                  <SafeHtml html={item.body} className="mt-3 pt-3 border-t border-zooborder" />
                ) : null}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="button" isLoading={captureMutation.isPending} onClick={save}>
            {t('clients:consents.save')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
